import { useCallback, useEffect, useRef } from 'react';

/** How long a burst of steps on one option may run before it is reported. */
export const COUNT_INTERCEPTION_WINDOW_MS = 600;

interface CountBurst {
  timer: ReturnType<typeof setTimeout>;
  /** The count the AI was last told. */
  sent: number;
  /** The latest step inside the burst, not yet reported. */
  pending?: { name: string; quantity: number };
}

const countMessage = (name: string, quantity: number) =>
  `${name} quantity changed to ${quantity}`;

/**
 * HITL-1753 — one interception per burst of count steps, not one per tap.
 * Stepping cream from ×1 to ×9 used to tell the AI eight times.
 *
 * The first step of a burst goes out at once: an interception is what stands
 * the AI down, and a hypothesis frame landing while it waited would rebuild the
 * cart over the agent's count. Later steps inside the window fold into one
 * trailing message with the final count — sent only if it differs from what the
 * AI was already told. The cart itself changes on every step; only the
 * announcement is coalesced. Pending messages are flushed on unmount.
 */
const useCoalescedCountInterception = (send: (description: string) => void) => {
  const bursts = useRef(new Map<string, CountBurst>());
  const sendRef = useRef(send);
  sendRef.current = send;

  const close = useCallback((key: string) => {
    const burst = bursts.current.get(key);
    bursts.current.delete(key);
    if (burst?.pending && burst.pending.quantity !== burst.sent) {
      sendRef.current(countMessage(burst.pending.name, burst.pending.quantity));
    }
  }, []);

  useEffect(() => {
    const open = bursts.current;
    return () => {
      Array.from(open.entries()).forEach(([key, burst]) => {
        clearTimeout(burst.timer);
        close(key);
      });
    };
  }, [close]);

  return useCallback(
    /** `key` names one option on one cart line; `name` is what the AI hears. */
    (key: string, name: string, quantity: number) => {
      const burst = bursts.current.get(key);
      if (!burst) {
        sendRef.current(countMessage(name, quantity));
        bursts.current.set(key, {
          sent: quantity,
          timer: setTimeout(() => close(key), COUNT_INTERCEPTION_WINDOW_MS),
        });
        return;
      }
      clearTimeout(burst.timer);
      burst.pending = { name, quantity };
      burst.timer = setTimeout(() => close(key), COUNT_INTERCEPTION_WINDOW_MS);
    },
    [close]
  );
};

export default useCoalescedCountInterception;
